import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSedes, createSede, updateSede, deleteSede, type Sede } from '../../api/sedes.api';
import { useAuth } from '../../context/AuthContext';
import { getSelectedSede, switchSede } from '../../utils/sede';

const emptyForm = { nombre: '', direccion: '', telefono: '', capacidadCarros: 0, capacidadMotos: 0 };

const errorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
};

export default function Sedes() {
  const { user, refreshUser } = useAuth();
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Sede | null>(null);
  const [formData, setFormData] = useState(emptyForm);

  const maxSedes = user?.company?.maxSedes ?? 1;
  const selectedSede = getSelectedSede();

  const loadSedes = async () => {
    try {
      setSedes(await getSedes());
    } catch (error) {
      toast.error(errorMessage(error, 'Error cargando las sedes'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSedes();
  }, []);

  const handleOpenModal = (sede?: Sede) => {
    setEditing(sede ?? null);
    setFormData(
      sede
        ? {
            nombre: sede.nombre,
            direccion: sede.direccion ?? '',
            telefono: sede.telefono ?? '',
            capacidadCarros: sede.capacidadCarros,
            capacidadMotos: sede.capacidadMotos,
          }
        : emptyForm,
    );
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        capacidadCarros: Number(formData.capacidadCarros) || 0,
        capacidadMotos: Number(formData.capacidadMotos) || 0,
      };
      if (editing) {
        await updateSede(editing.id, payload);
        toast.success('Sede actualizada');
      } else {
        await createSede(payload);
        toast.success('Sede creada. Ya puedes elegirla en la barra superior.');
      }
      setShowModal(false);
      await Promise.all([loadSedes(), refreshUser()]);
    } catch (error) {
      toast.error(errorMessage(error, 'Error guardando la sede'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (sede: Sede) => {
    try {
      await updateSede(sede.id, { activa: !sede.activa });
      toast.success(sede.activa ? 'Sede desactivada' : 'Sede activada');
      if (sede.activa && selectedSede === sede.id) switchSede(null);
      await Promise.all([loadSedes(), refreshUser()]);
    } catch (error) {
      toast.error(errorMessage(error, 'Error actualizando la sede'));
    }
  };

  const handleDelete = async (sede: Sede) => {
    if (!confirm(`¿Eliminar la sede "${sede.nombre}"? Si tiene historial se desactivará.`)) return;
    try {
      const result = await deleteSede(sede.id);
      toast.success(result.message);
      if (selectedSede === sede.id) switchSede(null);
      await Promise.all([loadSedes(), refreshUser()]);
    } catch (error) {
      toast.error(errorMessage(error, 'Error eliminando la sede'));
    }
  };

  const canCreate = sedes.length < maxSedes;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Sedes</h1>
          <p className="text-slate-500 mt-1">
            Cada sede tiene sus propios espacios, tarifas, turnos, caja e inventario. Vehículos, contratos y
            clientes se comparten en toda la empresa.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 whitespace-nowrap">
            {sedes.length} de {maxSedes} sede(s)
          </span>
          <button
            onClick={() => handleOpenModal()}
            disabled={!canCreate}
            title={canCreate ? 'Crear sede' : 'Alcanzaste el límite de sedes de tu plan'}
            className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line mr-2"></i>
            Nueva Sede
          </button>
        </div>
      </div>

      {!canCreate && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center gap-2">
          <i className="ri-information-line text-lg"></i>
          Alcanzaste el límite de {maxSedes} sede(s). Contacta a soporte para ampliarlo.
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sedes.map((sede) => (
            <div
              key={sede.id}
              className={`bg-white rounded-2xl border p-6 shadow-sm ${sede.activa ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-800 truncate">{sede.nombre}</h3>
                  <p className="text-sm text-slate-500 truncate">{sede.direccion || 'Sin dirección'}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${
                    sede.activa ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {sede.activa ? 'Activa' : 'Inactiva'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Capacidad carros</p>
                  <p className="font-semibold text-slate-800">{sede.capacidadCarros}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Capacidad motos</p>
                  <p className="font-semibold text-slate-800">{sede.capacidadMotos}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Usuarios</p>
                  <p className="font-semibold text-slate-800">{sede.usuarios ?? 0}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Equipos escritorio</p>
                  <p className="font-semibold text-slate-800">{sede.dispositivos ?? 0}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                {sede.activa && selectedSede !== sede.id && (
                  <button
                    onClick={() => switchSede(sede.id)}
                    className="px-3 py-2 bg-teal-50 text-teal-700 rounded-xl hover:bg-teal-100 transition text-sm font-medium"
                  >
                    <i className="ri-login-box-line mr-1"></i>
                    Trabajar aquí
                  </button>
                )}
                <button
                  onClick={() => handleOpenModal(sede)}
                  className="px-3 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition text-sm font-medium"
                >
                  <i className="ri-edit-line mr-1"></i>
                  Editar
                </button>
                <button
                  onClick={() => handleToggleActive(sede)}
                  className="px-3 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition text-sm font-medium"
                >
                  {sede.activa ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => handleDelete(sede)}
                  className="px-3 py-2 text-red-600 rounded-xl hover:bg-red-50 transition text-sm font-medium ml-auto"
                  title="Eliminar sede"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">{editing ? 'Editar Sede' : 'Nueva Sede'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100">
                <i className="ri-close-line text-xl text-slate-500"></i>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
              <input
                required
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Sede Norte"
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dirección</label>
              <input
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
              <input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Capacidad carros</label>
                <input
                  type="number"
                  min={0}
                  value={formData.capacidadCarros}
                  onChange={(e) => setFormData({ ...formData, capacidadCarros: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Capacidad motos</label>
                <input
                  type="number"
                  min={0}
                  value={formData.capacidadMotos}
                  onChange={(e) => setFormData({ ...formData, capacidadMotos: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
